import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { Button, Modal, List, Space, Tag, Typography, message, Card } from 'antd';
import { PlusOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { convertToFirestoreTask } from '../utils/crisisTaskEngine';

const { Text } = Typography;

const QuickTaskGenerator = ({ incidentId, missingTasks, onTasksGenerated }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [generating, setGenerating] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [modalVisible, setModalVisible] = useState(false);
  const [userOrgId, setUserOrgId] = useState(null);

  // Get user's orgId when component mounts
  React.useEffect(() => {
    const fetchUserOrgId = async () => {
      try {
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserOrgId(userDoc.data().orgId);
        }
      } catch (error) {
        console.error('Error fetching user orgId:', error);
      }
    };
    fetchUserOrgId();
  }, [currentUser.uid, db]);

  const toggleTaskSelection = (taskId) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const selectAllCritical = () => {
    const criticalTaskIds = missingTasks.filter(t => t.priority === 'critical').map(t => t.id);
    setSelectedTasks(new Set(criticalTaskIds));
  };

  const clearSelection = () => {
    setSelectedTasks(new Set());
  };

  const generateSelectedTasks = async () => {
    if (!userOrgId) {
      message.error('Organisation ID not found');
      return;
    }

    try {
      setGenerating(true);
      
      const tasksToGenerate = missingTasks.filter(task => selectedTasks.has(task.id));
      
      // Create tasks in Firestore
      const promises = tasksToGenerate.map(task => {
        const firestoreTask = convertToFirestoreTask(task, incidentId, userOrgId, currentUser.uid);
        return addDoc(collection(db, 'c3_tasks'), firestoreTask);
      });

      await Promise.all(promises);

      message.success(`Successfully generated ${tasksToGenerate.length} tasks`);
      setModalVisible(false);
      setSelectedTasks(new Set());
      
      // Notify parent component
      if (onTasksGenerated) {
        onTasksGenerated(tasksToGenerate);
      }

    } catch (error) {
      console.error('Error generating tasks:', error);
      message.error('Failed to generate tasks');
    } finally {
      setGenerating(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'red';
      case 'recommended': return 'orange';
      case 'optional': return 'blue';
      default: return 'default';
    }
  };

  const criticalTasks = missingTasks.filter(t => t.priority === 'critical');
  const recommendedTasks = missingTasks.filter(t => t.priority === 'recommended');

  if (!missingTasks || missingTasks.length === 0) {
    return null;
  }

  return (
    <>
      <Button 
        type="primary" 
        icon={<PlusOutlined />}
        onClick={() => setModalVisible(true)}
        style={{ marginTop: '8px' }}
      >
        Quick Add Missing Tasks ({missingTasks.length})
      </Button>

      <Modal
        title="🚀 Quick Task Generator"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="clear" 
            onClick={clearSelection}
            disabled={selectedTasks.size === 0}
          >
            Clear Selection
          </Button>,
          <Button 
            key="select-critical" 
            type="dashed"
            onClick={selectAllCritical}
            disabled={criticalTasks.length === 0}
          >
            Select All Critical ({criticalTasks.length})
          </Button>,
          <Button
            key="generate"
            type="primary"
            icon={generating ? <LoadingOutlined /> : <CheckCircleOutlined />}
            onClick={generateSelectedTasks}
            disabled={selectedTasks.size === 0 || generating}
            loading={generating}
          >
            Generate {selectedTasks.size} Selected Tasks
          </Button>
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Select the tasks you want to generate automatically. These tasks are identified as missing 
            based on your incident type and best practices.
          </Text>
        </div>

        {criticalTasks.length > 0 && (
          <Card 
            title="🚨 Critical Tasks" 
            size="small" 
            style={{ marginBottom: '16px' }}
            headStyle={{ backgroundColor: '#fff2f0', color: '#cf1322' }}
          >
            <List
              size="small"
              dataSource={criticalTasks}
              renderItem={task => (
                <List.Item
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: selectedTasks.has(task.id) ? '#e6f7ff' : 'transparent',
                    padding: '8px',
                    borderRadius: '4px',
                    border: selectedTasks.has(task.id) ? '1px solid #1890ff' : '1px solid transparent'
                  }}
                  onClick={() => toggleTaskSelection(task.id)}
                >
                  <List.Item.Meta
                    avatar={
                      <input 
                        type="checkbox" 
                        checked={selectedTasks.has(task.id)}
                        onChange={() => toggleTaskSelection(task.id)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    title={
                      <Space>
                        <Text strong>{task.name}</Text>
                        <Tag color={getPriorityColor(task.priority)}>{task.priority}</Tag>
                        <Tag color="blue">{task.assignedRole}</Tag>
                        {task.phase && <Tag color="purple">{task.phase}</Tag>}
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">{task.description}</Text>
                        {task.suggestedDueDate && (
                          <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Suggested due: {new Date(task.suggestedDueDate).toLocaleString()}
                            </Text>
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        )}

        {recommendedTasks.length > 0 && (
          <Card 
            title="⚠️ Recommended Tasks" 
            size="small"
            headStyle={{ backgroundColor: '#fff7e6', color: '#d46b08' }}
          >
            <List
              size="small"
              dataSource={recommendedTasks}
              renderItem={task => (
                <List.Item
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: selectedTasks.has(task.id) ? '#e6f7ff' : 'transparent',
                    padding: '8px',
                    borderRadius: '4px',
                    border: selectedTasks.has(task.id) ? '1px solid #1890ff' : '1px solid transparent'
                  }}
                  onClick={() => toggleTaskSelection(task.id)}
                >
                  <List.Item.Meta
                    avatar={
                      <input 
                        type="checkbox" 
                        checked={selectedTasks.has(task.id)}
                        onChange={() => toggleTaskSelection(task.id)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    title={
                      <Space>
                        <Text>{task.name}</Text>
                        <Tag color={getPriorityColor(task.priority)}>{task.priority}</Tag>
                        <Tag color="blue">{task.assignedRole}</Tag>
                        {task.phase && <Tag color="purple">{task.phase}</Tag>}
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">{task.description}</Text>
                        {task.suggestedDueDate && (
                          <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Suggested due: {new Date(task.suggestedDueDate).toLocaleString()}
                            </Text>
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        )}
      </Modal>
    </>
  );
};

export default QuickTaskGenerator; 